import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

/**
 * תבנית קלאסית — פס נייבי עליון, רקע לבן, קישוטי כוכב.
 * אלגנטית ופורמלית, כמו לוח הודעות מסודר בבית כנסת.
 */

const NAVY = '#1e3a5f';
const GOLD = '#9a7209';
const BORDER = '#e8e0d0';
const TEXT_PRIMARY = '#1e293b';
const TEXT_SECONDARY = '#5a6978';
const TEXT_MUTED = '#9a8f7a';
const BG_WARM = '#faf8f4';

const CATEGORY_LABELS = {
  tachanun: 'תחנון', hallel: 'הלל', prayerAdditions: 'תוספות תפילה',
  seasonalPrayers: 'תפילות עונתיות', seasonalTransitions: 'מעברים עונתיים',
  selichot: 'סליחות', weekdayPrayers: 'תפילות ימי חול',
  shabbatAdditions: 'תוספות שבת', motzaeiShabbat: 'מוצאי שבת',
  seasonalCustoms: 'מנהגים עונתיים', liturgy: 'פיוטים וקריאות',
  leyning: 'קריאת התורה', haftarah: 'הפטרה',
  mourningPeriods: 'ימי אבל ותענית', general: 'כללי',
};

// -- קו מפריד עם כוכב ---------------------------------------------------------
const OrnamentLine = () => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    my: 0.6,
  }}>
    <Box sx={{ flex: 1, height: '1px', background: GOLD, opacity: 0.25 }} />
    <Typography sx={{ color: GOLD, fontSize: '0.6rem', lineHeight: 1, opacity: 0.65 }}>✦</Typography>
    <Box sx={{ flex: 1, height: '1px', background: GOLD, opacity: 0.25 }} />
  </Box>
);

// -- כותרת קטגוריה מסודרת ------------------------------------------------------
const CategoryHeader = ({ label }) => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    mb: 0.4,
    mt: 0.5,
  }}>
    <Typography sx={{ color: GOLD, fontSize: '0.5rem', lineHeight: 1, opacity: 0.6 }}>✦</Typography>
    <Typography sx={{
      fontFamily: 'Frank Ruhl Libre, David, serif',
      fontSize: '0.58rem',
      fontWeight: 700,
      color: NAVY,
      letterSpacing: '0.08em',
      whiteSpace: 'nowrap',
    }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', background: BORDER }} />
  </Box>
);

// -- פריט בודד -----------------------------------------------------------------
const ItemRow = ({ item }) => {
  const isHigh = item.priority === 'high';

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: 1,
      py: 0.4,
      px: 0.75,
      borderRight: isHigh ? `3px solid ${GOLD}` : '3px solid transparent',
      background: isHigh ? '#fdf8ee' : 'transparent',
      borderRadius: '3px',
    }}>
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, David, serif',
        fontSize: '0.76rem',
        fontWeight: isHigh ? 700 : 500,
        color: isHigh ? NAVY : TEXT_PRIMARY,
        lineHeight: 1.45,
        flex: 1,
        minWidth: 0,
      }}>
        {item.label}
        {item.detail ? (
          <Typography component="span" sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.62rem',
            color: TEXT_MUTED,
            mr: 0.5,
          }}>
            {' '}({item.detail})
          </Typography>
        ) : null}
      </Typography>
      {item.value && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.72rem',
          color: isHigh ? NAVY : TEXT_SECONDARY,
          fontWeight: isHigh ? 600 : 400,
          lineHeight: 1.4,
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}>
          {item.value}
        </Typography>
      )}
    </Box>
  );
};

// -- עזר: קיבוץ לפי קטגוריה ---------------------------------------------------
function groupByCategory(items) {
  const map = {};
  items.forEach((item) => {
    const cat = item.category || 'general';
    if (!map[cat]) map[cat] = [];
    map[cat].push(item);
  });
  return Object.entries(map);
}

// -- יום lookahead בודד --------------------------------------------------------
const LookaheadDay = ({ dayData }) => (
  <Box sx={{ mt: 1.25 }}>
    <OrnamentLine />
    <Typography sx={{
      fontFamily: 'Frank Ruhl Libre, David, serif',
      fontSize: '0.72rem',
      fontWeight: 700,
      color: NAVY,
      textAlign: 'center',
      mb: 0.5,
    }}>
      {dayData.hebrewDate?.hebrew || dayData.date}
    </Typography>
    {dayData.items.length === 0 ? (
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, David, serif',
        fontSize: '0.65rem',
        color: TEXT_MUTED,
        textAlign: 'center',
      }}>
        אין ענייני יום מיוחדים
      </Typography>
    ) : (
      groupByCategory(dayData.items).map(([cat, catItems]) => (
        <Box key={cat} sx={{ mb: 0.5 }}>
          <CategoryHeader label={CATEGORY_LABELS[cat] || cat} />
          {catItems.map((item, i) => (
            <ItemRow key={item.id || `${cat}-${i}`} item={item} />
          ))}
        </Box>
      ))
    )}
  </Box>
);

// == תבנית קלאסית ==============================================================
const Classic = ({
  today,
  items,
  lookaheadItems,
  lookaheadDays,
  loading,
  error,
  title,
  showTitle,
}) => {
  const hebrewDateStr = today?.hebrewDate?.hebrew || '';
  const grouped = groupByCategory(items || []);

  return (
    <Box sx={{
      background: '#ffffff',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      direction: 'rtl',
      overflow: 'hidden',
    }}>
      {/* פס נייבי עליון */}
      {showTitle && (
        <Box sx={{
          background: NAVY,
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          flexShrink: 0,
        }}>
          <Typography sx={{ color: GOLD, fontSize: '0.55rem', lineHeight: 1 }}>✦</Typography>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.72rem',
            fontWeight: 700,
            color: '#fff',
            letterSpacing: '0.12em',
          }}>
            {title}
          </Typography>
          <Typography sx={{ color: GOLD, fontSize: '0.55rem', lineHeight: 1 }}>✦</Typography>
        </Box>
      )}

      {/* גוף */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        padding: '14px 16px',
        background: BG_WARM,
        '&::-webkit-scrollbar': { width: 3 },
        '&::-webkit-scrollbar-thumb': { background: BORDER, borderRadius: 2 },
      }}>
        {/* תאריך עברי */}
        {hebrewDateStr && (
          <Box sx={{ textAlign: 'center', mb: 1.25 }}>
            <OrnamentLine />
            <Typography sx={{
              fontFamily: 'Frank Ruhl Libre, David, serif',
              fontSize: '0.95rem',
              fontWeight: 700,
              color: NAVY,
              letterSpacing: '0.03em',
            }}>
              {hebrewDateStr}
            </Typography>
            <OrnamentLine />
          </Box>
        )}

        {/* loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={18} sx={{ color: NAVY }} />
          </Box>
        )}

        {/* error */}
        {error && (
          <Typography sx={{
            fontSize: '0.68rem',
            color: '#c0392b',
            textAlign: 'center',
            mb: 0.5,
          }}>
            שגיאה בטעינה
          </Typography>
        )}

        {/* ריק */}
        {grouped.length === 0 && !loading && (
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.75rem',
            color: TEXT_MUTED,
            textAlign: 'center',
            mt: 2,
          }}>
            אין ענייני יום מיוחדים להיום
          </Typography>
        )}

        {/* פריטי היום */}
        {grouped.map(([cat, catItems]) => (
          <Box key={cat} sx={{ mb: 0.5 }}>
            <CategoryHeader label={CATEGORY_LABELS[cat] || cat} />
            {catItems.map((item, i) => (
              <ItemRow key={item.id || `${cat}-${i}`} item={item} />
            ))}
          </Box>
        ))}

        {/* ימי lookahead */}
        {lookaheadDays > 0 && lookaheadItems && lookaheadItems.length > 0 && (
          lookaheadItems.map((dayData, i) => (
            <LookaheadDay key={dayData.date || i} dayData={dayData} />
          ))
        )}
      </Box>
    </Box>
  );
};

export default Classic;
